import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { parseISO } from 'date-fns'
import { baseURL, headers } from '@/v2/constants/requests'
import { useGetDoctors } from '@/v2/hooks/useDoctors'
import {
  getColumnsByRoom,
  getColumnsByDoctor,
  getOffHours,
  getEvents,
} from '../../utils'

// Hook for fetching rooms from REST API
const fetchRooms = async () => {
  const res = await fetch(`${baseURL}/v2/rest/rooms`, {
    method: 'GET',
    headers,
  })
  if (!res.ok) {
    throw new Error(`Error fetching rooms: ${res.statusText}`)
  }
  return res.json()
}

const useRooms = () => useQuery({ queryKey: ['rooms'], queryFn: fetchRooms })

// Shared daily-calendar data preparation used by the day timeline (DayTimeline,
// desktop + mobile) and the week grid (Day → Calendar). Keeping the rooms/doctors
// fetches and the column/event/off-hours derivations in one place means the
// renderers stay in sync — they only differ in how they paint the same data and,
// via `isMobile`, whether virtual doctor columns are included.
export const useDayData = ({
  date,
  subjectView,
  typeFilter,
  selectedColumn,
  isMobile,
  appointments,
  timeOffs,
  clinicHours,
}) => {
  const {
    data: rooms,
    isLoading: loadingRooms,
    isError: errorRooms,
  } = useRooms()
  const {
    data: doctors,
    loading: loadingDoctors,
    error: errorDoctors,
  } = useGetDoctors()

  const loading = loadingRooms || loadingDoctors
  const error = errorRooms || errorDoctors

  const columns = useMemo(() => {
    if (!rooms || !doctors) return []
    let allColumns
    if (selectedColumn) {
      if (selectedColumn.startsWith('room-')) {
        allColumns = getColumnsByRoom(rooms, doctors, false)
      } else if (selectedColumn.startsWith('doctor-')) {
        allColumns = getColumnsByDoctor(doctors)
      }
    }
    if (!allColumns) {
      const includeVirtual = !isMobile
      allColumns =
        subjectView === 'room'
          ? getColumnsByRoom(rooms, doctors, includeVirtual)
          : getColumnsByDoctor(doctors)
    }
    return selectedColumn
      ? allColumns.filter(col => col.id === selectedColumn)
      : allColumns
  }, [rooms, doctors, selectedColumn, subjectView, isMobile])

  const filteredAppointments = useMemo(
    () =>
      typeFilter.length
        ? appointments.filter(({ appointmentType }) =>
            typeFilter.includes(appointmentType.id)
          )
        : appointments,
    [appointments, typeFilter]
  )

  const offHours = useMemo(() => {
    if (!doctors || !columns.length) return []
    return getOffHours(clinicHours, doctors, columns, date)
  }, [clinicHours, doctors, columns, date])

  const events = useMemo(
    () => getEvents(filteredAppointments, timeOffs, columns, subjectView),
    [filteredAppointments, timeOffs, columns, subjectView]
  )

  return { rooms, doctors, columns, events, offHours, loading, error }
}

// Convenience: build the RTDailyCalendar props from a 'yyyy-MM-dd' date string.
export const realtimeProps = date => ({
  day: parseISO(date).getDate(),
  monthNumber: parseISO(date).getMonth() + 1,
  year: parseISO(date).getFullYear(),
})
